import { Operation } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';

/**
 * Gateway statuses Traefik returns while a backend task is being replaced
 * during a rolling update: the request was routed to a task that is draining
 * or not listening yet. Any other status is an answer from the backend itself
 * and retrying it would only repeat the same answer.
 */
const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);

export const HTTP_RETRY_MAX_ATTEMPTS = 5;

const isQuery = (operation: Operation) => {
  const definition = getMainDefinition(operation.query);
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'query'
  );
};

/**
 * Decides whether a failed HTTP operation is retried.
 *
 * Only queries: a gateway error can be returned after the request already
 * reached a backend, so a retried mutation (survey submission, assessment
 * creation) could apply twice. Subscriptions never get here, they travel on
 * the WebSocket branch, which reconnects and re-subscribes on its own.
 *
 * Only transport failures (no status at all) and gateway statuses. An error
 * carrying a GraphQL result is excluded even on a 5xx: the upload link has
 * already emitted that result, and RetryLink would emit a second one.
 */
export const shouldRetryHttpOperation = (
  error: any,
  operation: Operation,
): boolean => {
  if (!error || !isQuery(operation)) return false;
  if (error.result) return false;
  if (typeof error.statusCode === 'number') {
    return RETRYABLE_STATUS_CODES.has(error.statusCode);
  }
  // fetch rejects with a TypeError when the connection is refused or reset
  // ("Failed to fetch", "Load failed" on Safari, "NetworkError…" on Firefox).
  return error instanceof TypeError;
};

/**
 * Retry for the HTTP branch only, so a backend rollout does not surface as a
 * network error in the components. Backoff 300 ms → 3 s with jitter over
 * {@link HTTP_RETRY_MAX_ATTEMPTS} attempts, i.e. roughly 6-8 s in total, which
 * covers the seconds Traefik keeps routing to a task that is going away.
 */
export const createHttpRetryLink = () =>
  new RetryLink({
    delay: { initial: 300, max: 3000, jitter: true },
    attempts: {
      max: HTTP_RETRY_MAX_ATTEMPTS,
      retryIf: shouldRetryHttpOperation,
    },
  });
